/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// INTERFACES
import { LiftInterface } from "../../interfaces/Lift.interface";
import { BankInterface } from "../../interfaces/Bank.interface";
import {
  MonitoringViewTypeEnum,
  MonitoringDeviceConfigurationInterface,
} from "../../interfaces/Monitoring.interface";
// CONSTANTS
import { BLANK_LIFT } from "../../constants/lifts";
import { BLANK_BANK } from "../../constants/bank";

export interface MonitoringState {
  openDrawerLiftActions: boolean;
  liftSelected: LiftInterface;
  bankSelected: BankInterface;
  channelId: string;
  serverId: string;
  controlPanelTab: {
    liftType: string;
    tab: string;
  };
  controlPanelDrawerPlacement: "right" | "left";
  openModalControlPanelLift: boolean;
  openModalControlPanelBank: boolean;
  openModalSaveGrid: boolean;
  openModalManageGrids: boolean;
  enableDragAndDrop: boolean;
  showFullView: boolean;
  monitoringGridType: MonitoringViewTypeEnum;
  idGridSelected: string;
  cascaderSelectMultipleSelectionOptions: [];
  topViewLayout: [];
  detailViewLayout: [];
  statusViewLayout: [];
  serviceViewLayout: [];
  refreshPage: boolean;
  sendNewMessage: any;
  showQueueInfo: boolean;
  openGlobalTerminal: boolean;
  totpIsValid: boolean;
  monitorViewIsLoading: boolean;
  socketConnection: string;
  edgeServerIsConnected: boolean;
  emulatorIsConnected: boolean;
  terminalResetTable: boolean;
  terminalDownloadReport: boolean;
  favoriteGridName: string;
  hardcodedCommands: {
    id: string;
    enum_id: number;
    message_type_id: number;
    device_type: string;
    message_content_type: number;
    short_message: string;
    description: string;
  }[];
  clearCascader: boolean;
  showHeaderConfiguration: boolean;
  openMonitoringDeviceConfigurationDrawer: boolean;
  monitoringDeviceConfiguration: MonitoringDeviceConfigurationInterface;
  terminalAutoScroll: boolean;
  showTerminalTable: boolean;
  showTerminalJsonViewer: boolean;
  openReplayPanel: boolean;
  replayFilterBankId: string;
  replayScannedBankIds: string[];
  terminalShowCarHallCalls: boolean;
  stopSocketConnection: boolean;
  terminalShowBankFloors: boolean;
  terminalIconMode: "svg" | "text";
  binaryCaptureActive: boolean;
  binaryCaptureReady: boolean;
  sendCommand: {
    sendCommand: boolean;
    serverId: string;
    body: {
      command: any;
      position: number;
      active: boolean;
      bankId: string;
      liftId: string;
    };
  };
}

const initialState: MonitoringState = {
  openDrawerLiftActions: false,
  liftSelected: { ...BLANK_LIFT },
  bankSelected: { ...BLANK_BANK },
  controlPanelTab: {
    liftType: "ELEVATOR",
    tab: "FAULTS",
  },
  controlPanelDrawerPlacement: "right",
  openModalControlPanelLift: false,
  openModalControlPanelBank: false,
  openModalSaveGrid: false,
  openModalManageGrids: false,
  serverId: "",
  channelId: "",
  enableDragAndDrop: false,
  showFullView: false,
  monitoringGridType: 0,
  idGridSelected: "",
  cascaderSelectMultipleSelectionOptions: [],
  topViewLayout: [],
  detailViewLayout: [],
  statusViewLayout: [],
  serviceViewLayout: [],
  refreshPage: false,
  sendNewMessage: {},
  showQueueInfo: false,
  openGlobalTerminal: false,
  totpIsValid: false,
  monitorViewIsLoading: false,
  socketConnection: process.env.REACT_APP_EDGE_SERVER_CONNECTION || "",
  edgeServerIsConnected: false,
  emulatorIsConnected: false,
  terminalResetTable: false,
  terminalDownloadReport: false,
  favoriteGridName: "",
  hardcodedCommands: [],
  clearCascader: false,
  showHeaderConfiguration: true,
  openMonitoringDeviceConfigurationDrawer: false,
  monitoringDeviceConfiguration: {
    showElevators: true,
    showEscalators: true,
    showMovingwalks: true,
    showGpio: true,
    viewType: "server",
  },
  terminalAutoScroll: false,
  showTerminalTable: true,
  showTerminalJsonViewer: true,
  openReplayPanel: false,
  replayFilterBankId: "",
  replayScannedBankIds: [],
  terminalShowCarHallCalls: false,
  stopSocketConnection: false,
  terminalShowBankFloors: false,
  terminalIconMode: "svg" as "svg" | "text",
  binaryCaptureActive: false,
  binaryCaptureReady: false,
  sendCommand: {
    sendCommand: false,
    serverId: "",
    body: { command: null, position: 0, active: true, bankId: "", liftId: "" },
  },
};

export const monitoringSlice = createSlice({
  name: "monitoring",
  initialState,
  reducers: {
    updateOpenDrawerLiftActions: (state, action: PayloadAction<boolean>) => {
      state.openDrawerLiftActions = action.payload;
    },
    updateLiftSelected: (state, action: PayloadAction<LiftInterface>) => {
      state.liftSelected = action.payload;
    },
    updateBankSelected: (state, action: PayloadAction<BankInterface>) => {
      state.bankSelected = action.payload;
    },
    updateModalControlPanelTab: (state, action: PayloadAction<any>) => {
      state.controlPanelTab = action.payload;
    },
    updateOpenModalControlPanelLift: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.openModalControlPanelLift = action.payload;
    },
    updateOpenModalControlPanelBank: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.openModalControlPanelBank = action.payload;
    },
    updateOpenModalSaveGrid: (state, action: PayloadAction<boolean>) => {
      state.openModalSaveGrid = action.payload;
    },
    updateOpenModalManageGrids: (state, action: PayloadAction<boolean>) => {
      state.openModalManageGrids = action.payload;
    },
    updateServerId: (state, action: PayloadAction<string>) => {
      state.serverId = action.payload;
    },
    updateChannelId: (state, action: PayloadAction<string>) => {
      state.channelId = action.payload;
    },
    updateEnableDragAndDrop: (state, action: PayloadAction<boolean>) => {
      state.enableDragAndDrop = action.payload;
    },
    updateShowFullView: (state, action: PayloadAction<boolean>) => {
      state.showFullView = action.payload;
    },
    updateMonitoringGridType: (
      state,
      action: PayloadAction<MonitoringViewTypeEnum>
    ) => {
      state.monitoringGridType = action.payload;
    },
    updateIdGridSelected: (state, action: PayloadAction<string>) => {
      state.idGridSelected = action.payload;
    },
    updateCascaderSelectMultipleSelectionOptions: (
      state,
      action: PayloadAction<any>
    ) => {
      state.cascaderSelectMultipleSelectionOptions = action.payload;
    },
    updateTopViewLayout: (state, action: PayloadAction<any>) => {
      state.topViewLayout = action.payload;
    },
    updateDetailViewLayout: (state, action: PayloadAction<any>) => {
      state.detailViewLayout = action.payload;
    },
    updateStatusViewLayout: (state, action: PayloadAction<any>) => {
      state.statusViewLayout = action.payload;
    },
    updateServiceViewLayout: (state, action: PayloadAction<any>) => {
      state.serviceViewLayout = action.payload;
    },
    updateRefreshPage: (state, action: PayloadAction<boolean>) => {
      state.refreshPage = action.payload;
    },
    updateSendNewMessage: (state, action: PayloadAction<any>) => {
      state.sendNewMessage = action.payload;
    },
    updateShowQueueInfo: (state, action: PayloadAction<boolean>) => {
      state.showQueueInfo = action.payload;
    },
    updateOpenGlobalTerminal: (state, action: PayloadAction<boolean>) => {
      state.openGlobalTerminal = action.payload;
    },
    updateSocketConnection: (state, action: PayloadAction<string>) => {
      state.socketConnection = action.payload;
    },
    updateTotpIsValid: (state, action: PayloadAction<boolean>) => {
      state.totpIsValid = action.payload;
    },
    updateMonitorViewIsLoading: (state, action: PayloadAction<boolean>) => {
      state.monitorViewIsLoading = action.payload;
    },
    updateEdgeServerIsConnected: (state, action: PayloadAction<boolean>) => {
      state.edgeServerIsConnected = action.payload;
    },
    updateEmulatorIsConnected: (state, action: PayloadAction<boolean>) => {
      state.emulatorIsConnected = action.payload;
    },
    updateTerminalResetTable: (state, action: PayloadAction<boolean>) => {
      state.terminalResetTable = action.payload;
    },
    updateTerminalDownloadReport: (state, action: PayloadAction<boolean>) => {
      state.terminalDownloadReport = action.payload;
    },
    updateControlPanelDrawerPlacement: (
      state,
      action: PayloadAction<"right" | "left">
    ) => {
      state.controlPanelDrawerPlacement = action.payload;
    },
    updateFavoriteGridName: (state, action: PayloadAction<string>) => {
      state.favoriteGridName = action.payload;
    },
    updateClearCascader: (state, action: PayloadAction<boolean>) => {
      state.clearCascader = action.payload;
    },
    updateShowHeaderConfiguration: (state, action: PayloadAction<boolean>) => {
      state.showHeaderConfiguration = action.payload;
    },
    updateOpenMonitoringDeviceConfigurationDrawer: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.openMonitoringDeviceConfigurationDrawer = action.payload;
    },
    updateMonitoringDeviceConfiguration: (
      state,
      action: PayloadAction<MonitoringDeviceConfigurationInterface>
    ) => {
      state.monitoringDeviceConfiguration = action.payload;
    },
    updateTerminalAutoScroll: (state, action: PayloadAction<boolean>) => {
      state.terminalAutoScroll = action.payload;
    },
    updateShowTerminalJsonViewer: (state, action: PayloadAction<boolean>) => {
      state.showTerminalJsonViewer = action.payload;
    },
    updateShowTerminalTable: (state, action: PayloadAction<boolean>) => {
      state.showTerminalTable = action.payload;
    },
    updateOpenReplayPanel: (state, action: PayloadAction<boolean>) => {
      state.openReplayPanel = action.payload;
      if (!action.payload) {
        state.replayFilterBankId = "";
        state.replayScannedBankIds = [];
      }
    },
    updateReplayFilterBankId: (state, action: PayloadAction<string>) => {
      state.replayFilterBankId = action.payload;
    },
    updateReplayScannedBankIds: (state, action: PayloadAction<string[]>) => {
      state.replayScannedBankIds = action.payload;
    },
    updateTerminalShowCarHallCalls: (state, action: PayloadAction<boolean>) => {
      state.terminalShowCarHallCalls = action.payload;
    },
    updateStopSocketConnection: (state, action: PayloadAction<boolean>) => {
      state.stopSocketConnection = action.payload;
    },
    updateTerminalShowBankFloors: (state, action: PayloadAction<boolean>) => {
      state.terminalShowBankFloors = action.payload;
    },
    updateTerminalIconMode: (state, action: PayloadAction<"svg" | "text">) => {
      state.terminalIconMode = action.payload;
    },
    updateBinaryCaptureActive: (state, action: PayloadAction<boolean>) => {
      state.binaryCaptureActive = action.payload;
    },
    updateBinaryCaptureReady: (state, action: PayloadAction<boolean>) => {
      state.binaryCaptureReady = action.payload;
    },
    updateSendCommand: (
      state,
      action: PayloadAction<{
        sendCommand: boolean;
        serverId: string;
        body: any;
      }>
    ) => {
      state.sendCommand = action.payload;
    },
    updateHardCommands: (
      state,
      action: PayloadAction<
        {
          id: string;
          enum_id: number;
          message_type_id: number;
          device_type: string;
          message_content_type: number;
          short_message: string;
          description: string;
        }[]
      >
    ) => {
      state.hardcodedCommands = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  updateOpenDrawerLiftActions,
  updateLiftSelected,
  updateBankSelected,
  updateModalControlPanelTab,
  updateOpenModalControlPanelLift,
  updateOpenModalControlPanelBank,
  updateOpenModalSaveGrid,
  updateOpenModalManageGrids,
  updateServerId,
  updateChannelId,
  updateEnableDragAndDrop,
  updateShowFullView,
  updateMonitoringGridType,
  updateIdGridSelected,
  updateCascaderSelectMultipleSelectionOptions,
  updateDetailViewLayout,
  updateTopViewLayout,
  updateStatusViewLayout,
  updateServiceViewLayout,
  updateRefreshPage,
  updateSendNewMessage,
  updateShowQueueInfo,
  updateSocketConnection,
  updateOpenGlobalTerminal,
  updateTotpIsValid,
  updateMonitorViewIsLoading,
  updateEdgeServerIsConnected,
  updateEmulatorIsConnected,
  updateTerminalResetTable,
  updateTerminalDownloadReport,
  updateControlPanelDrawerPlacement,
  updateFavoriteGridName,
  updateHardCommands,
  updateClearCascader,
  updateOpenMonitoringDeviceConfigurationDrawer,
  updateMonitoringDeviceConfiguration,
  updateShowHeaderConfiguration,
  updateTerminalAutoScroll,
  updateShowTerminalJsonViewer,
  updateShowTerminalTable,
  updateOpenReplayPanel,
  updateReplayFilterBankId,
  updateReplayScannedBankIds,
  updateTerminalShowCarHallCalls,
  updateStopSocketConnection,
  updateTerminalShowBankFloors,
  updateTerminalIconMode,
  updateBinaryCaptureActive,
  updateBinaryCaptureReady,
  updateSendCommand,
} = monitoringSlice.actions;

export default monitoringSlice.reducer;
